import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { ApiError } from "../api.ts";
import { RecentActivity } from "../components/activity/RecentActivity.tsx";
import { Breadcrumbs } from "../components/Breadcrumbs.tsx";
import { AccessPanel } from "../components/file/AccessPanel.tsx";
import { FileHeader } from "../components/file/FileHeader.tsx";
import styles from "../components/file/FilePage.module.css";
import { SandboxPanel } from "../components/file/SandboxPanel.tsx";
import { VersionsPanel } from "../components/file/VersionsPanel.tsx";
import { Icon } from "../components/Icon.tsx";
import { StatePanel } from "../components/StatePanel.tsx";
import { ShareLinksPanel } from "../components/shares/ShareLinksPanel.tsx";
import ui from "../components/ui.module.css";
import { usePageDropTarget } from "../lib/drop.ts";
import { isHtml } from "../lib/kinds.ts";
import { audienceQuery, groupsQuery, messageOf, nodeQuery, versionsQuery } from "../lib/queries.ts";

export const Route = createFileRoute("/files/$id")({
  loader: async ({ context: { queryClient }, params: { id } }) => {
    const [node] = await Promise.all([
      queryClient.ensureQueryData(nodeQuery(id)),
      queryClient.ensureQueryData(versionsQuery(id)),
      queryClient.ensureQueryData(audienceQuery(id)),
      queryClient.ensureQueryData(groupsQuery),
    ]);
    if (node.kind === "folder") throw redirect({ to: "/folders/$id", params: { id } });
  },
  component: FilePage,
  pendingComponent: FilePending,
  errorComponent: FileError,
});

function FilePage() {
  const { id } = Route.useParams();
  const { data: node } = useSuspenseQuery(nodeQuery(id));

  useEffect(() => {
    document.title = `${node.name} · Vault`;
  }, [node.name]);

  usePageDropTarget({
    kind: "version",
    nodeId: node.id,
    name: node.name,
    parentId: node.parentId,
  });

  return (
    <div className={styles.page}>
      {node.ancestors.length > 0 ? <Breadcrumbs ancestors={node.ancestors} /> : <BackLink />}
      <FileHeader node={node} />
      <div className={styles.panels}>
        <AccessPanel node={node} />
        <div className={styles.side}>
          <ShareLinksPanel node={node} />
          <VersionsPanel node={node} />
          {isHtml(node.contentType, node.name) ? <SandboxPanel node={node} /> : null}
        </div>
      </div>
      <RecentActivity nodeId={node.id} />
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/" className={`${ui.button} ${styles.back}`} data-variant="frame">
      <Icon name="back" />
      All files
    </Link>
  );
}

function FilePending() {
  return (
    <div className={`${styles.page} ${styles.pending}`} aria-busy="true">
      <BackLink />
      <div className={styles.pendingHeader}>
        <span className={`${ui.skeleton} ${styles.pendingSticker}`} />
        <span className={`${ui.skeleton} ${styles.pendingTitle}`} />
        <span className={`${ui.skeleton} ${styles.pendingMeta}`} />
      </div>
      <div className={styles.panels}>
        <div className={`${ui.card} ${styles.panel}`}>
          <span className={`${ui.skeleton} ${styles.pendingTitleBar}`} />
          <span className={`${ui.skeleton} ${styles.pendingField}`} />
          <PendingRows count={2} />
        </div>
        <div className={`${ui.card} ${styles.panel}`}>
          <span className={`${ui.skeleton} ${styles.pendingTitleBar}`} />
          <PendingRows count={3} />
        </div>
      </div>
    </div>
  );
}

function PendingRows({ count }: { count: number }) {
  return (
    <div>
      {["first", "second", "third"].slice(0, count).map((row) => (
        <div key={row} className={styles.pendingRow}>
          <span className={`${ui.skeleton} ${styles.pendingDot}`} />
          <span className={`${ui.skeleton} ${styles.pendingLine}`} />
        </div>
      ))}
    </div>
  );
}

function FileError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
    return (
      <StatePanel
        icon="files"
        tone="info"
        title="No file here"
        detail="The file was deleted or the address is wrong."
      >
        <Link to="/" className={ui.button}>
          All files
        </Link>
      </StatePanel>
    );
  }
  return (
    <StatePanel icon="alert" title="This file did not load" detail={messageOf(error)}>
      <Link to="/" className={ui.button}>
        All files
      </Link>
      <button
        type="button"
        className={ui.button}
        data-variant="primary"
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
